import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Chip,
  Typography,
  Box,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { AuditLog } from '../../types/admin';

/**
 * Props for AuditLogTable component
 */
interface AuditLogTableProps {
  logs: AuditLog[];
  loading?: boolean;
}

type Order = 'asc' | 'desc';
type OrderBy = 'timestamp' | 'action' | 'userEmail';

/**
 * AuditLogTable Component
 * Displays audit log entries in a sortable, paginated table
 *
 * Features:
 * - Sortable columns
 * - Pagination
 * - Expandable rows for details
 * - Color-coded action types
 * - Timestamp formatting
 */
const AuditLogTable: React.FC<AuditLogTableProps> = ({ logs, loading = false }) => {
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sorting state
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('timestamp');

  // Expanded row state
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  /**
   * Handle sort request
   */
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Handle page change
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /**
   * Handle rows per page change
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Toggle row expansion
   */
  const handleExpandRow = (logId: string) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  /**
   * Sort logs
   */
  const sortedLogs = [...logs].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (orderBy) {
      case 'timestamp':
        aValue = a.timestamp.getTime();
        bValue = b.timestamp.getTime();
        break;
      case 'action':
        aValue = a.action;
        bValue = b.action;
        break;
      case 'userEmail':
        aValue = a.userEmail;
        bValue = b.userEmail;
        break;
      default:
        return 0;
    }

    if (order === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Paginate logs
  const paginatedLogs = sortedLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  /**
   * Get action color
   */
  const getActionColor = (
    action: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    if (action.includes('delete') || action.includes('suspend') || action.includes('reject')) {
      return 'error';
    }
    if (action.includes('approve') || action.includes('create')) {
      return 'success';
    }
    if (action.includes('update') || action.includes('edit')) {
      return 'warning';
    }
    return 'info';
  };

  /**
   * Format action name
   */
  const formatAction = (action: string): string => {
    return action.replace(/_/g, ' ').toUpperCase();
  };

  /**
   * Empty state
   */
  if (logs.length === 0 && !loading) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No Audit Logs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No audit log entries match your current filters.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell sortDirection={orderBy === 'timestamp' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'timestamp'}
                  direction={orderBy === 'timestamp' ? order : 'asc'}
                  onClick={() => handleRequestSort('timestamp')}
                >
                  Timestamp
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'action' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'action'}
                  direction={orderBy === 'action' ? order : 'asc'}
                  onClick={() => handleRequestSort('action')}
                >
                  Action
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'userEmail' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'userEmail'}
                  direction={orderBy === 'userEmail' ? order : 'asc'}
                  onClick={() => handleRequestSort('userEmail')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell>Target</TableCell>
              <TableCell>IP Address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLogs.map((log) => (
              <React.Fragment key={log.id}>
                <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                  <TableCell>
                    {log.details && (
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRow(log.id)}
                          aria-label="expand row"
                        >
                          {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(log.timestamp, 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(log.timestamp, 'HH:mm:ss')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatAction(log.action)}
                      size="small"
                      color={getActionColor(log.action)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{log.userEmail}</Typography>
                    {log.userName && (
                      <Typography variant="caption" color="text.secondary">
                        {log.userName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.targetUserId && (
                      <>
                        <Typography variant="body2" noWrap>
                          {log.targetUserEmail || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {log.targetUserId.substring(0, 8)}...
                        </Typography>
                      </>
                    )}
                    {log.contentType && (
                      <>
                        <Typography variant="body2">{log.contentType}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {log.contentId?.substring(0, 12)}...
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {log.ipAddress || 'N/A'}
                    </Typography>
                  </TableCell>
                </TableRow>
                {log.details && (
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                      <Collapse in={expandedRow === log.id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <InfoIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Details
                            </Typography>
                          </Box>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <pre
                              style={{
                                margin: 0,
                                fontSize: '12px',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                maxHeight: '200px',
                                overflow: 'auto',
                              }}
                            >
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </Paper>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={logs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default AuditLogTable;
